import { Controller } from "@hotwired/stimulus"

// Scroll-driven story for a stack of Rivers.
//
// The server renders the rivers and an empty pinned column. On connect the
// media of each river is copied into that column as a "responder", and an
// IntersectionObserver per river marks the highest visible one active: its
// tracker gets `in-viewport`, its responder `tracker-in-viewport`, and a
// video responder plays. The stylesheet does the fading and stacking from
// those classes, and the pagination dots from :nth-child.
//
// A visitor who prefers reduced motion gets plain rivers: the enabled class
// comes off and nothing is observed.
const LARGE_BREAKPOINT = 1012

export default class extends Controller {
  static targets = ["visuals", "pagination", "content", "tracker"]

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.element.classList.remove("RiverStoryScroll--enabled")
      this.element.querySelectorAll(".RiverStoryScroll__content-stack").forEach(river => {
        river.classList.remove("RiverStoryScroll__content-stack")
      })
      return
    }

    this.responders = this.trackerTargets.map(tracker => this.buildResponder(tracker))
    this.visible = new Set()
    this.lastActive = null
    this.observers = this.trackerTargets.flatMap((tracker, index) => this.observe(tracker, index))
  }

  disconnect() {
    ;(this.observers || []).forEach(observer => observer.disconnect())
  }

  buildResponder(tracker) {
    const media = tracker.querySelector("img, video")
    const responder = document.createElement("div")
    responder.className = "RiverStoryScroll__visual-scroll-responder tracker-outside-viewport"

    if (media instanceof HTMLVideoElement) {
      const video = document.createElement("video")
      video.playsInline = true
      video.muted = true
      video.preload = "auto"
      video.className = "RiverStoryScroll__image"
      const source = document.createElement("source")
      source.src = media.querySelector("source")?.src || media.src
      source.type = "video/mp4"
      video.append(source)
      responder.append(video)
    } else if (media) {
      const image = document.createElement("img")
      image.className = "RiverStoryScroll__image"
      image.src = media.src
      image.alt = ""
      responder.append(image)
    }

    // The pagination dots must stay last so the :nth-child rules hold.
    this.paginationTarget.before(responder)
    const dot = document.createElement("div")
    dot.className = "RiverStoryScroll__pagination-dot"
    this.paginationTarget.append(dot)

    return responder
  }

  // Two observers per tracker, one tuned for landscape and one for portrait
  // viewports; each only acts when its orientation is current, as upstream.
  observe(tracker, index) {
    const large = window.innerWidth >= LARGE_BREAKPOINT
    const landscape = () => window.innerWidth / window.innerHeight >= 0.78

    const landscapeObserver = new IntersectionObserver(([entry]) => {
      if (landscape()) this.update(index, entry.isIntersecting)
    }, large ? { threshold: 0.8 } : { rootMargin: "-80% 0% -19.99% 0%", threshold: 0 })

    const portraitObserver = new IntersectionObserver(([entry]) => {
      if (!landscape()) this.update(index, entry.isIntersecting)
    }, large ? { threshold: 0.8 } : { rootMargin: "-65% 0% -34.99% 0%", threshold: 0 })

    landscapeObserver.observe(tracker)
    portraitObserver.observe(tracker)
    return [landscapeObserver, portraitObserver]
  }

  update(index, isIntersecting) {
    if (isIntersecting) this.visible.add(index)
    else this.visible.delete(index)

    const active = this.visible.size > 0 ? Math.max(...this.visible) : this.lastActive
    if (active === null) return
    this.lastActive = active

    this.trackerTargets.forEach((tracker, i) => {
      tracker.classList.toggle("in-viewport", i === active)
      tracker.classList.toggle("outside-viewport", i !== active)
    })

    this.responders.forEach((responder, i) => {
      responder.classList.toggle("tracker-in-viewport", i === active)
      responder.classList.toggle("tracker-outside-viewport", i !== active)
      const video = responder.querySelector("video")
      if (!video) return
      if (i === active) video.play().catch(() => {})
      else video.pause()
    })
  }
}
